import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DialogContentSafeArea } from '@/components/ui/dialog-safe-area';
import type { ExportData, ExportType } from '../../types';

interface PreviewDialogProps {
  open: boolean;
  onClose: () => void;
  exportType: ExportType | null;
  exportData: ExportData | null;
  uploadedUrl: string | null;
}

export function PreviewDialog({ open, onClose, exportType, exportData, uploadedUrl }: PreviewDialogProps) {
  if (!exportData) {
    return null;
  }

  const getTitle = () => {
    const titles: Record<string, string> = {
      pdf: 'PDF Preview',
      png: 'Image Preview',
      csv: 'CSV Preview',
    };
    return exportType ? titles[exportType] || 'File Preview' : 'File Preview';
  };

  const renderPreview = () => {
    if (exportType === 'png') {
      return (
        <div className="space-y-4">
          <div className="border rounded-lg overflow-hidden bg-muted">
            <img
              src={exportData.dataUrl}
              alt="Calendar preview"
              className="w-full h-auto"
            />
          </div>
          <div className="text-sm text-muted-foreground bg-blue-50 dark:bg-blue-950 p-3 rounded-md">
            <p className="font-medium text-blue-900 dark:text-blue-100 mb-1">💡 How to save:</p>
            <p className="text-blue-800 dark:text-blue-200">Right-click the image above and select "Save Image As..." to download it to your computer.</p>
          </div>
        </div>
      );
    }

    if (exportType === 'pdf') {
      if (!uploadedUrl) {
        return (
          <div className="flex flex-col items-center justify-center h-48 space-y-3">
            <p className="text-sm text-muted-foreground">PDF preview not available</p>
            <p className="text-xs text-muted-foreground">Upload file first from the export dialog</p>
          </div>
        );
      }

      const previewUrl = `${uploadedUrl}?preview=true`;

      return (
        <div className="space-y-4">
          <div className="border rounded-lg overflow-hidden" style={{ height: '70vh' }}>
            <embed
              src={previewUrl}
              type="application/pdf"
              width="100%"
              height="100%"
              className="w-full h-full"
            />
          </div>
          <div className="text-sm text-muted-foreground bg-blue-50 dark:bg-blue-950 p-3 rounded-md">
            <p className="font-medium text-blue-900 dark:text-blue-100 mb-1">💡 How to save:</p>
            <p className="text-blue-800 dark:text-blue-200">Use the download button in the PDF viewer above, or close this dialog and click "Open" to download.</p>
          </div>
        </div>
      );
    }

    if (exportType === 'csv' && exportData.text) {
      const lines = exportData.text.split('\n').filter(line => line.trim());
      const headers = lines[0]?.split(',') || [];
      const rows = lines.slice(1).map(line => line.split(','));

      return (
        <div className="space-y-4">
          <div className="border rounded-lg overflow-auto max-h-60">
            <table className="w-full text-xs">
              <thead className="bg-muted sticky top-0">
                <tr>
                  {headers.map((header, i) => (
                    <th key={i} className="px-2 py-1 text-left font-medium border-b">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} className="border-b hover:bg-muted/50">
                    {row.map((cell, j) => (
                      <td key={j} className="px-2 py-1">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">CSV Data (copy and paste into Excel/Sheets):</label>
            <textarea
              readOnly
              value={exportData.text}
              className="w-full h-32 p-2 text-xs font-mono border rounded-md bg-muted"
              onClick={(e) => (e.target as HTMLTextAreaElement).select()}
            />
          </div>
          <div className="text-sm text-muted-foreground bg-blue-50 dark:bg-blue-950 p-3 rounded-md">
            <p className="font-medium text-blue-900 dark:text-blue-100 mb-1">💡 How to use:</p>
            <p className="text-blue-800 dark:text-blue-200">Click the textarea above to select all, then copy (Ctrl+C / Cmd+C) and paste into Excel or Google Sheets.</p>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContentSafeArea className="max-w-4xl overflow-y-auto [scrollbar-gutter:stable]">
        <DialogHeader>
          <DialogTitle>{getTitle()}</DialogTitle>
          <DialogDescription>
            Preview and save your file using the instructions below
          </DialogDescription>
        </DialogHeader>
        {renderPreview()}
      </DialogContentSafeArea>
    </Dialog>
  );
}
